// /src/components/items/CreateItemModal.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "components/ui/Button";
import { Modal } from "components/ui/Modal";
import {
  getCategories,
  createCategory,
  createItem,
  createIncomingAct,
} from "api/items";
import { listSuppliers, createSupplier } from "api/suppliers";

/* ===== Ikonkalar (Icons) ===== */
const TagIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A1 1 0 012 10V5a1 1 0 011-1h5a1 1 0 01.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
      clipRule="evenodd"
    />
  </svg>
);
const ScaleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M3 6a3 3 0 013-3h10a1 1 0 110 2H6a1 1 0 00-1 1v10a1 1 0 11-2 0V6z"
      clipRule="evenodd"
    />
  </svg>
);
const PlusCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
      clipRule="evenodd"
    />
  </svg>
);
const PlusIconMini = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-4 w-4"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
      clipRule="evenodd"
    />
  </svg>
);

/* ===== Komponent ===== */
export const CreateItemModal = ({ open, onClose, onCreated }) => {
  // form state
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [unit, setUnit] = useState("dona");
  const [minStock, setMinStock] = useState("");
  const [initQty, setInitQty] = useState("");
  const [initPrice, setInitPrice] = useState("");
  const [supplier, setSupplier] = useState(""); // supplier_id
  const [isConsumable, setIsConsumable] = useState(false);
  const [categoryId, setCategoryId] = useState("");
  const [cats, setCats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [catLoading, setCatLoading] = useState(false);
  const [supLoading, setSupLoading] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});

  // refs
  const nameRef = useRef(null);

  // derived values
  const minStockNum = useMemo(
    () => (minStock === "" ? "" : Number(minStock)),
    [minStock]
  );
  const initQtyNum = useMemo(
    () => (initQty === "" ? "" : Number(initQty)),
    [initQty]
  );
  const initPriceNum = useMemo(
    () => (initPrice === "" ? "" : Number(initPrice)),
    [initPrice]
  );

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (minStock !== "" && (Number.isNaN(minStockNum) || minStockNum < 0))
      return false;
    if (initQty !== "" && (Number.isNaN(initQtyNum) || initQtyNum < 0))
      return false;
    if (initPrice !== "" && (Number.isNaN(initPriceNum) || initPriceNum < 0))
      return false;
    return true;
  }, [
    name,
    minStock,
    minStockNum,
    initQty,
    initQtyNum,
    initPrice,
    initPriceNum,
  ]);

  const resetForm = useCallback(() => {
    setSku("");
    setName("");
    setUnit("dona");
    setMinStock("");
    setInitQty("");
    setInitPrice("");
    setSupplier("");
    setIsConsumable(false);
    setCategoryId("");
    setError(null);
    setTouched({});
  }, []);

  // load categories and suppliers when open
  useEffect(() => {
    if (!open) return;
    resetForm();
    const loadData = async () => {
      try {
        setError(null);
        // Categories
        const catList = (await getCategories()) || [];
        setCats(catList);

        // Suppliers
        const supResponse = await listSuppliers({ limit: 100 }); // Fetch up to 100 suppliers for dropdown
        let supList = [];
        if (Array.isArray(supResponse)) {
          supList = supResponse;
        } else if (supResponse && Array.isArray(supResponse.rows)) {
          supList = supResponse.rows;
        }
        setSuppliers(supList);
      } catch (e) {
        console.error(e);
        setError(e.message || "Ma'lumotlarni yuklashda xatolik");
      }
    };
    loadData();
    setTimeout(() => nameRef.current?.focus(), 0);
  }, [open, resetForm]);

  const handleClose = useCallback(() => {
    if (!loading && !catLoading && !supLoading) {
      onClose?.();
      resetForm();
    }
  }, [loading, catLoading, supLoading, onClose, resetForm]);

  const handleCreateCategory = useCallback(
    async (newName) => {
      if (!newName?.trim() || catLoading) return;
      setCatLoading(true);
      try {
        const created = await createCategory({ name: newName.trim() });
        const updated = [
          created,
          ...cats.filter((c) => c.id !== created.id),
        ].sort((a, b) => a.name.localeCompare(b.name));
        setCats(updated);
        setCategoryId(String(created.id));
      } catch (e) {
        setError(e.message || "Kategoriyani yaratishda xatolik");
      } finally {
        setCatLoading(false);
      }
    },
    [cats, catLoading]
  );

  const handleCreateSupplier = useCallback(
    async (newName) => {
      if (!newName?.trim() || supLoading) return;
      setSupLoading(true);
      try {
        const created = await createSupplier({ name: newName.trim() });
        const updated = [
          created,
          ...suppliers.filter((s) => s.id !== created.id),
        ].sort((a, b) => a.name.localeCompare(b.name));
        setSuppliers(updated);
        setSupplier(String(created.id));
      } catch (e) {
        setError(e.message || "Yetkazib beruvchini yaratishda xatolik");
      } finally {
        setSupLoading(false);
      }
    },
    [suppliers, supLoading]
  );

  const submit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      setTouched({
        name: true,
        minStock: true,
        initQty: true,
        initPrice: true,
      });
      if (!canSubmit) return;

      setLoading(true);
      setError(null);
      try {
        const minStockSafe = minStock === "" ? 0 : Number(minStockNum) || 0;
        const initQtySafe = initQty === "" ? 0 : Number(initQtyNum) || 0;
        const initPriceSafe = initPrice === "" ? 0 : Number(initPriceNum) || 0;
        const supplierSafe = supplier.trim();

        const item = await createItem({
          sku: sku.trim() || null,
          name: name.trim(),
          category_id: categoryId || null,
          unit: unit.trim() || "dona",
          min_stock: minStockSafe,
          price: initPriceSafe,
          supplier_id: supplier || null,
          is_consumable: isConsumable,
        });

        // 2) Initial incoming act if qty > 0
        if (initQtySafe > 0) {
          // Find supplier name if selected, otherwise fallback
          const selectedSup = suppliers.find(s => String(s.id) === String(supplierSafe));
          const counterpartyName = selectedSup ? selectedSup.name : "Boshlang'ich qoldiq";

          await createIncomingAct({
            counterparty: counterpartyName,
            positions: [
              { item_id: item.id, qty: initQtySafe, price: initPriceSafe },
            ],
          });
        }

        onCreated?.(item);
        handleClose();
      } catch (e) {
        setError(e.message || "Saqlashda xatolik");
      } finally {
        setLoading(false);
      }
    },
    [
      canSubmit,
      minStock,
      minStockNum,
      initQty,
      initQtyNum,
      initPrice,
      initPriceNum,
      supplier,
      sku,
      name,
      categoryId,
      unit,
      onCreated,
      handleClose,
      isConsumable,
      suppliers
    ]
  );

  // Ctrl/⌘+Enter
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        submit();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, submit]);

  const fieldError = useCallback(
    (key) => {
      if (!touched[key]) return null;
      if (key === "name" && !name.trim()) return "Nomini ko'rsating";
      if (
        key === "minStock" &&
        minStock !== "" &&
        (Number.isNaN(minStockNum) || minStockNum < 0)
      )
        return "≥ 0";
      if (
        key === "initQty" &&
        initQty !== "" &&
        (Number.isNaN(initQtyNum) || initQtyNum < 0)
      )
        return "≥ 0";
      if (
        key === "initPrice" &&
        initPrice !== "" &&
        (Number.isNaN(initPriceNum) || initPriceNum < 0)
      )
        return "≥ 0";
      return null;
    },
    [
      touched,
      name,
      minStock,
      minStockNum,
      initQty,
      initQtyNum,
      initPrice,
      initPriceNum,
    ]
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <PlusCircleIcon />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">Yangi tovar</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              Tovar ma'lumotlarini to'ldiring
            </p>
          </div>
        </div>
      }
      className="bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 max-w-3xl"
      footer={
        <div className="flex items-center justify-between w-full pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-2">
            {canSubmit ? (
              <>
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-slate-200 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
                  Ctrl
                </kbd>
                <span>+</span>
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-slate-200 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg">
                  Enter
                </kbd>
                <span className="ml-1">saqlash uchun</span>
              </>
            ) : (
              <span className="text-amber-600">
                ⚠️ Majburiy maydonlarni to'ldiring
              </span>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleClose}
              disabled={loading || catLoading || supLoading}
              className="px-4 text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              Bekor qilish
            </Button>
            <Button
              onClick={submit}
              disabled={loading || catLoading || supLoading || !canSubmit}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 shadow-sm"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Yaratilmoqda...
                </span>
              ) : (
                "Tovarni yaratish"
              )}
            </Button>
          </div>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-6">
        <FormSection
          title="Asosiy ma'lumotlar"
          icon={<TagIcon />}
          description="Tovar nomi va artikulini kiriting"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  Artikul (SKU)
                  <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                    (ixtiyoriy)
                  </span>
                </span>
              </label>
              <input
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="NB-001"
                className={inputStyles}
              />
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                Tovarning unikal identifikatori
              </p>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  Tovar nomi
                  <span className="text-red-500">*</span>
                </span>
              </label>
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                placeholder="Masalan, Noutbuk Lenovo ThinkPad X1"
                className={`${inputStyles} ${fieldError("name")
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : ""
                  }`}
              />
              {fieldError("name") && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <span>⚠️</span>
                  {fieldError("name")}
                </p>
              )}
            </div>
          </div>

          {/* Kategoriya + tez yaratish tugmasi */}
          <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  <span className="flex items-center gap-1">
                    Kategoriya
                    <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                      (ixtiyoriy)
                    </span>
                  </span>
                </label>
                <SearchableSelect
                  options={cats}
                  value={categoryId}
                  onChange={setCategoryId}
                  onCreate={handleCreateCategory}
                  placeholder="Kategoriyani tanlang yoki yangisini yarating..."
                  createLabel="Yangi kategoriya yaratish"
                  loading={catLoading}
                />
              </div>
              <div className="pt-7">
                <Button
                  type="button"
                  variant="outline"
                  disabled={catLoading}
                  onClick={async () => {
                    const v = prompt("Yangi kategoriya nomi:");
                    if (v && v.trim()) await handleCreateCategory(v.trim());
                  }}
                  title="Yangi kategoriya yaratish"
                  className="whitespace-nowrap text-gray-700 dark:text-slate-200 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <PlusIconMini /> Yaratish
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
              Kategoriya tovarlarni guruhlashga yordam beradi
            </p>
          </div>
        </FormSection>

        <FormSection
          title="O'lchov birliklari va qoldiqlar"
          icon={<ScaleIcon />}
          description="O'lchov birligi va minimal qoldiqni sozlang"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  O'lchov birligi
                  <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                    (standart: dona)
                  </span>
                </span>
              </label>
              <input
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="dona, kg, l, m va h.k."
                className={inputStyles}
              />
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                Masalan: dona, kg, l, m, quti
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  Minimal qoldiq
                  <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                    (ixtiyoriy)
                  </span>
                </span>
              </label>
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                onWheel={(e) => e.currentTarget.blur()}
                value={minStock}
                onChange={(e) => setMinStock(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, minStock: true }))}
                placeholder="0"
                className={`${inputStyles} ${fieldError("minStock")
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : ""
                  }`}
              />
              {fieldError("minStock") ? (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <span>⚠️</span>
                  {fieldError("minStock")}
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                  Ushbu darajaga yetganda ogohlantirish beriladi
                </p>
              )}
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Boshlang'ich qoldiq va narx"
          icon={<PlusCircleIcon />}
          description="Boshlang'ich miqdor va narxni ko'rsating (ixtiyoriy)"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  Boshlang'ich miqdor
                  <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                    (ixtiyoriy)
                  </span>
                </span>
              </label>
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                onWheel={(e) => e.currentTarget.blur()}
                value={initQty}
                onChange={(e) => setInitQty(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, initQty: true }))}
                placeholder="0"
                className={`${inputStyles} ${fieldError("initQty")
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : ""
                  }`}
              />
              {fieldError("initQty") ? (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <span>⚠️</span>
                  {fieldError("initQty")}
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                  Agar ko'rsatilsa, avtomatik ravishda kirim akti yaratiladi
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  Birbirlik narxi
                  <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                    (ixtiyoriy)
                  </span>
                </span>
              </label>
              <input
                type="number"
                step="any"
                min="0"
                inputMode="decimal"
                onWheel={(e) => e.currentTarget.blur()}
                value={initPrice}
                onChange={(e) => setInitPrice(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, initPrice: true }))}
                placeholder="0"
                className={`${inputStyles} ${fieldError("initPrice")
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : ""
                  }`}
              />
              {fieldError("initPrice") ? (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <span>⚠️</span>
                  {fieldError("initPrice")}
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
                  Tovar kartochkasida va kirim aktida saqlanadi
                </p>
              )}
            </div>
          </div>

          {/* Yetkazib beruvchi + tez yaratish */}
          <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  <span className="flex items-center gap-1">
                    Yetkazib beruvchi
                    <span className="text-xs font-normal text-gray-400 dark:text-slate-500">
                      (ixtiyoriy)
                    </span>
                  </span>
                </label>
                <SearchableSelect
                  options={suppliers}
                  value={supplier}
                  onChange={setSupplier}
                  onCreate={handleCreateSupplier}
                  placeholder="Yetkazib beruvchini tanlang..."
                  createLabel="Yangi yetkazib beruvchi yaratish"
                  loading={supLoading}
                />
              </div>
              <div className="pt-7">
                <Button
                  type="button"
                  variant="outline"
                  disabled={supLoading}
                  onClick={async () => {
                    const v = prompt("Yangi yetkazib beruvchi nomi:");
                    if (v && v.trim()) await handleCreateSupplier(v.trim());
                  }}
                  title="Yangi yetkazib beruvchi yaratish"
                  className="whitespace-nowrap text-gray-700 dark:text-slate-200 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <PlusIconMini /> Yaratish
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1.5">
              Agar yetkazib beruvchi va boshlang'ich qoldiq ko'rsatilsa, u kirim aktida ishlatiladi
            </p>
          </div>
        </FormSection>

        <FormSection
          title="Xususiyatlar"
          icon={<TagIcon />}
          description="Tovar uchun qo'shimcha parametrlar"
        >
          <label className="flex items-center gap-3 p-3 border border-gray-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
            <input
              type="checkbox"
              checked={isConsumable}
              onChange={(e) => setIsConsumable(e.target.checked)}
              className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600"
            />
            <div>
              <span className="block font-medium text-gray-900 dark:text-slate-100">
                Sarflanadigan material
              </span>
              <span className="block text-xs text-gray-500 dark:text-slate-400">
                Tovar qaytarib olinmaydi (qaytarishda ogohlantirish bo'ladi)
              </span>
            </div>
          </label>
        </FormSection>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200 flex items-start gap-2">
            <span className="text-red-600 dark:text-red-400 text-lg">⚠️</span>
            <div>
              <strong className="font-semibold">Xatolik:</strong> {error}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};

/* ===== Lokal UI-yordamchilar ===== */
const inputStyles =
  "w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500";

const FormSection = ({ title, icon, children, description }) => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <span className="text-blue-600 dark:text-blue-400">{icon}</span>
      <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
    </div>
    {description && (
      <p className="text-xs text-gray-500 dark:text-slate-400 ml-7">{description}</p>
    )}
    <div className="p-6 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm space-y-4">
      {children}
    </div>
  </div>
);

const SearchableSelect = ({
  options,
  value,
  onChange,
  onCreate,
  placeholder,
  createLabel = "Yangi yaratish",
  loading,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = useMemo(
    () => options.find((opt) => String(opt.id) === String(value)),
    [options, value]
  );

  const filteredOptions =
    query === ""
      ? options
      : options.filter((opt) =>
        opt.name.toLowerCase().includes(query.toLowerCase())
      );

  const handleCreate = async (name) => {
    if (!onCreate || !name?.trim() || loading) return;
    await onCreate(name);
    setIsOpen(false);
    setQuery("");
  };

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsOpen(false);
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-11 text-left px-4 py-2.5 ${inputStyles} flex items-center justify-between hover:border-gray-400 dark:hover:border-slate-500 transition-colors`}
        disabled={loading}
      >
        {selectedOption ? (
          <span className="font-medium text-gray-800 dark:text-slate-100">
            {selectedOption.name}
          </span>
        ) : (
          <span className="text-gray-500 dark:text-slate-400">{placeholder}</span>
        )}
        <svg
          className={`h-5 w-5 text-gray-400 dark:text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""
            }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-slate-700 z-20 p-2 space-y-1 border dark:border-slate-700">
          <input
            type="text"
            placeholder="Qidirish..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`w-full px-3 py-2 ${inputStyles}`}
            disabled={loading}
            autoFocus
          />
          <ul className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <li
                  key={opt.id}
                  onMouseDown={() => {
                    onChange(String(opt.id));
                    setIsOpen(false);
                    setQuery("");
                  }}
                  className="px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer text-gray-800 dark:text-slate-200"
                >
                  {opt.name}
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-gray-500 text-xs">
                Natija yo'q
              </li>
            )}

            {query &&
              !filteredOptions.some(
                (o) => o.name.toLowerCase() === query.toLowerCase()
              ) && (
                <li
                  onMouseDown={() => handleCreate(query)}
                  className="px-3 py-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer text-blue-600 dark:text-blue-400 font-semibold"
                >
                  + {createLabel} «{query}»
                </li>
              )}
          </ul>
        </div>
      )}
    </div>
  );
};
